import {
  Control,
  Controller,
  SubmitHandler,
  UseFormHandleSubmit,
  UseFormReset,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import UsersAutocomplete from '../../../shared/users-autocomplete';
import { DatePicker } from '@mui/x-date-pickers';
import { MobileSearchFormFilter } from './mobile-management-page';
import {
  DateRangePicker,
  SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import { UseMutateFunction } from '@tanstack/react-query';
import { MobileDevice, MobileSearchFilter } from '../../../../types';
import { useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ClearAll from '@mui/icons-material/ClearAll';
import Search from '@mui/icons-material/Search';

interface MobileSearchGridFiltersProps {
  control: Control<MobileSearchFormFilter>;
  watch: UseFormWatch<MobileSearchFormFilter>;
  setValue: UseFormSetValue<MobileSearchFormFilter>;
  isLoading: boolean;
  mutate: UseMutateFunction<MobileDevice[], Error, MobileSearchFilter, unknown>;
  handleSubmit: UseFormHandleSubmit<
    MobileSearchFormFilter,
    MobileSearchFormFilter
  >;
  isDirty: boolean;
  reset: UseFormReset<MobileSearchFormFilter>;
}

export default function MobileSearchGridFilters({
  control,
  watch,
  setValue,
  isLoading,
  mutate,
  handleSubmit,
  isDirty,
  reset,
}: MobileSearchGridFiltersProps) {
  const [error, setError] = useState(false);
  const statusOptions = [
    {
      value: 'approved',
      displayValue: 'Approved',
    },
    {
      value: 'account_wiped',
      displayValue: 'Account Wiped',
    },
    {
      value: 'blocked',
      displayValue: 'Blocked',
    },
    {
      value: 'pending',
      displayValue: 'Pending',
    },
    {
      value: 'wiped',
      displayValue: 'Wiped',
    },
    {
      value: 'unprovisioned',
      displayValue: 'Unprovisioned',
    },
    {
      value: 'all',
      displayValue: 'All',
    },
  ];

  const selectedType = watch('type');
  const syncType = watch('syncType') || 'inclusiveRange';

  // The default values for comparison
  const defaultValues: MobileSearchFormFilter = {
    email: '',
    status: 'all',
    type: 'both',
    syncStartDate: null,
    syncEndDate: null,
    syncType: 'inclusiveRange',
    dateRange: [null, null],
  };

  const onSubmit: SubmitHandler<MobileSearchFormFilter> = (data) => {
    const isFormUnchanged =
      JSON.stringify(data) === JSON.stringify(defaultValues);
    const missingDate =
      data.syncType !== 'inclusiveRange' && !data.syncStartDate;
    if (isFormUnchanged || missingDate) {
      setError(true);
    } else {
      setError(false);
      const newSearch: MobileSearchFilter = {
        email: data.email,
        status: data.status === 'all' ? '' : data.status,
        type: data.type === 'both' ? '' : data.type,
        syncType: data.syncType,
        syncStartDate:
          data.dateRange &&
          data.dateRange[0] &&
          data.syncType === 'inclusiveRange'
            ? data.dateRange[0].startOf('day').format('YYYY-MM-DDTHH:mm:ss')
            : data.syncType === 'beforeDate' && data.syncStartDate
            ? data.syncStartDate.endOf('day').format('YYYY-MM-DDTHH:mm:ss')
            : data.syncType === 'afterDate' && data.syncStartDate
            ? data.syncStartDate.startOf('day').format('YYYY-MM-DDTHH:mm:ss')
            : null,
        syncEndDate:
          data.dateRange &&
          data.dateRange[1] &&
          data.syncType === 'inclusiveRange'
            ? data.dateRange[1].endOf('day').format('YYYY-MM-DDTHH:mm:ss')
            : data.syncType === 'beforeDate' && data.syncEndDate
            ? data.syncEndDate.endOf('day').format('YYYY-MM-DDTHH:mm:ss')
            : data.syncType === 'afterDate' && data.syncEndDate
            ? data.syncEndDate.startOf('day').format('YYYY-MM-DDTHH:mm:ss')
            : null,
      };
      mutate(newSearch);
    }
  };

  return (
    <Card sx={{ p: 1.5, height: '100%', overflowY: 'auto' }}>
      <Stack gap={2}>
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Box>
              <UsersAutocomplete
                variant="outlined"
                autocompleteValue={field.value}
                setAutoCompleteValue={(newValue) => {
                  field.onChange(newValue);
                  if (error) {
                    setError(false);
                  }
                }}
              />
            </Box>
          )}
        />
        <Divider />
        <FormControl>
          <FormLabel>Operating System Type</FormLabel>
          <RadioGroup
            aria-labelledby="radio-buttons-group-label"
            name="radio-buttons-group"
            value={selectedType}
            onChange={(event) => {
              setValue('type', event.currentTarget.value as 'android' | 'ios', {
                shouldDirty: true,
              });
              if (error) {
                setError(false);
              }
            }}
            row
          >
            <FormControlLabel
              value="both"
              control={<Radio size="small" />}
              label="Both"
            />
            <FormControlLabel
              value="android"
              control={<Radio size="small" />}
              label="Android"
            />
            <FormControlLabel
              value="ios"
              control={<Radio size="small" />}
              label="iOS"
            />
          </RadioGroup>
        </FormControl>
        <Divider />
        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <TextField
              inputProps={{
                id: 'status',
              }}
              InputLabelProps={{
                htmlFor: 'status',
              }}
              label="Status"
              select
              value={field.value}
              onChange={(newValue) => {
                field.onChange(newValue);
                if (error) {
                  setError(false);
                }
              }}
              fullWidth
              autoComplete="off"
            >
              {statusOptions.map((option) => (
                <MenuItem value={option.value} key={option.displayValue}>
                  {option.displayValue}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Divider />
        <FormControl>
          <FormLabel>{`Last Sync Date Options (Inclusive)`}</FormLabel>
          <RadioGroup
            aria-labelledby="sync-date-options"
            name="sync-date-options"
            defaultValue="inclusiveRange"
            value={syncType}
            onChange={(event) => {
              setValue(
                'syncType',
                event.currentTarget.value as
                  | 'inclusiveRange'
                  | 'beforeDate'
                  | 'afterDate'
              );
              setValue('dateRange', [null, null]);
              setValue('syncStartDate', null);
            }}
            row
          >
            <FormControlLabel
              value="inclusiveRange"
              control={<Radio size="small" />}
              label="Range"
            />
            <FormControlLabel
              value="beforeDate"
              control={<Radio size="small" />}
              label="Before Date"
            />
            <FormControlLabel
              value="afterDate"
              control={<Radio size="small" />}
              label="After Date"
            />
          </RadioGroup>
        </FormControl>
        {syncType === 'inclusiveRange' && (
          <Controller
            name={'dateRange'}
            control={control}
            rules={{
              validate: {
                endAfterStart: (value) => {
                  if (
                    value &&
                    value[1] &&
                    value[0] &&
                    value[1].isBefore(value[0])
                  ) {
                    return 'End date must be after the start date';
                  }
                  return true;
                },
                bothDatesRequired: (value) => {
                  if (
                    (value && value[0] && !value[1]) ||
                    (value && !value[0] && value[1])
                  ) {
                    return 'Please provide both dates.';
                  }
                  return true;
                },
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <DateRangePicker
                {...field}
                disableHighlightToday
                disableFuture
                label={'Last Sync Date Range'}
                slots={{ field: SingleInputDateRangeField }}
                slotProps={{
                  textField: {
                    error: !!error?.message,
                    helperText: error?.message ? error.message : ' ',
                  },
                }}
              />
            )}
          />
        )}
        {syncType !== 'inclusiveRange' && (
          <Controller
            name="syncStartDate"
            control={control}
            render={({ field: { value, onChange } }) => (
              <DatePicker
                value={value || null}
                onChange={(newValue) => {
                  if (newValue) {
                    onChange(newValue);
                    setValue('syncStartDate', newValue);
                  }
                  if (error) {
                    setError(false);
                  }
                }}
                disableFuture
                label="Last Sync Date"
                sx={{ pb: 3 }}
              />
            )}
          />
        )}
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          startIcon={<Search />}
          disabled={isLoading || !isDirty}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            reset();
            if (error) {
              setError(false);
            }
          }}
          startIcon={<ClearAll />}
          disabled={isLoading || !isDirty}
        >
          Reset Filters
        </Button>
        {error ? (
          <Box sx={{ width: 337 }}>
            <Grow in={true}>
              <Alert severity="error">
                {'Please apply at least one filter to your search.'}
              </Alert>
            </Grow>
          </Box>
        ) : null}
      </Stack>
    </Card>
  );
}
